/**
 * Quelles conversations rédigent, pour l'affichage (#859).
 *
 * **Le contrat de ce hook est un NON-ÉVÉNEMENT** : il ne doit pas re-rendre son
 * appelant quand un bloc arrive. Le hub réveille ses abonnés à chaque bloc
 * — c'est ce qui fait avancer le texte dans le canvas — mais le bandeau et la
 * liste des conversations, eux, ne changent qu'au démarrage et à la fin d'un
 * travail. La liste des conversations est montée dans la coque, sur toutes les
 * pages : la faire re-rendre au rythme du flux coûterait partout.
 *
 * Même patron que `useSessionBadges` (`GlobalConversationSidebar`) pour le store
 * des sessions, et pour la même raison : un abonnement manuel avec comparaison,
 * jamais un sélecteur qui reconstruit un tableau à chaque lecture — ce dernier
 * casse le contrat `getSnapshot` de `useSyncExternalStore` et boucle.
 */

import * as React from 'react';
import { redactionStreamHub } from './redactionStreamHub';

/** Référence stable pour « aucune rédaction » — le cas de très loin le plus fréquent. */
const AUCUNE: readonly string[] = [];

function memesIdentifiants(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((id, i) => id === b[i]);
}

/**
 * Les conversations qui rédigent, avec une référence **stable** tant que
 * l'ensemble ne change pas.
 */
export function useConversationsQuiRedigent(): readonly string[] {
  const [conversations, setConversations] = React.useState<readonly string[]>(AUCUNE);

  React.useEffect(() => {
    const relire = (): void => {
      const suivantes = redactionStreamHub.conversationsQuiRedigent();
      setConversations((precedentes) => {
        if (memesIdentifiants(precedentes, suivantes)) return precedentes;
        return suivantes.length === 0 ? AUCUNE : suivantes;
      });
    };
    // Relire au montage : une rédaction peut déjà tourner quand l'afficheur
    // arrive — c'est même le cas nominal depuis #857, puisque l'écoute survit à
    // la navigation qui a démonté l'afficheur précédent.
    relire();
    return redactionStreamHub.souscrire(relire);
  }, []);

  return conversations;
}
